import { Component, computed, EventEmitter, inject, Output } from '@angular/core';
import { v3ComponentContext } from '../services/v3-component-context';
import { FeatureFlagStore } from '../stores/feature-flags.store';
import { V3ScoreBar } from '../v3-score-bar/v3-score-bar';
import { V3ScoreCard } from '../v3-score-card/v3-score-card';
import { V3OverviewCardHeader } from '../v3-overview-card-header/v3-overview-card-header';
import {
  computeScoreTilesWithKey,
  FundamentalScoreSystemKey,
  hasRatedFundamentalScore,
  resolveFundamentalScoresContent,
  resolveFundamentalScoresUpdatedAt,
  ScoreTileWithKey,
} from '../services/api/fundamental-scores';
import { toV3ColorVar } from './score-color';

interface V3ScoreTile extends ScoreTileWithKey {
  v3ColorVar: string;
}

// V3's main-page "Scores" card — restyles V2's FundamentalScoresOverview. See CLAUDE.md.
@Component({
  selector: 'app-v3-scores-overview',
  imports: [V3ScoreBar, V3ScoreCard, V3OverviewCardHeader],
  standalone: true,
  templateUrl: './v3-scores-overview.html',
  styleUrl: './v3-scores-overview.scss',
})
export class V3ScoresOverview {
  private readonly ctx = v3ComponentContext();
  private readonly featureFlagStore = inject(FeatureFlagStore);

  // Emits the clicked tile's system key so the detail sub-page can open with that category
  // already selected; undefined for a generic "Details"/header click (no specific tile).
  @Output() navigate = new EventEmitter<FundamentalScoreSystemKey | undefined>();

  readonly isFinnet = this.ctx.layout.isFinnet;
  readonly isFinnetDesktop = this.ctx.layout.isFinnetDesktop;
  readonly isZeroDesktop = this.ctx.layout.isZeroDesktop;

  readonly isGuestMode = this.ctx.isGuestMode;
  readonly isTraderfoxEnabled = this.featureFlagStore.isTraderfoxEnabled;

  private readonly content = computed(() =>
    resolveFundamentalScoresContent(this.ctx.assetsStore.fundamentalScores(), this.isGuestMode()),
  );

  private readonly hasRating = computed(() => {
    const content = this.content();
    return !!content && hasRatedFundamentalScore(content);
  });

  // Same gating as FundamentalScoresOverview's V2 card (core.ts's hasFundamentalScoresRating).
  readonly visible = computed(
    () => this.isTraderfoxEnabled() && (this.isGuestMode() || this.hasRating()),
  );

  readonly tiles = computed<V3ScoreTile[]>(() =>
    computeScoreTilesWithKey(this.content() ?? {}).map((tile) => ({
      ...tile,
      v3ColorVar: toV3ColorVar(tile.colorVar),
    })),
  );

  readonly updatedAt = computed(() => resolveFundamentalScoresUpdatedAt(this.isGuestMode()));

  onNavigate(key?: FundamentalScoreSystemKey) {
    this.navigate.emit(key);
  }
}
